import React, { useState } from 'react';
import TablePagination from '@/components/shared/TablePagination';
import './VacancyCalendarTable.scss';
import { safeToLowerCase } from '@/utils/stringUtils';

interface VacancyCalendarData {
  id: string;
  date: string;
  vacancy: {
    status: 'PENDING' | 'DELAY' | 'VACANT' | 'WARNING';
  };
  tenant?: {
    name: string;
    email: string;
    avatar?: string;
  };
  unit: string;
  property: {
    name: string;
    type?: 'residential' | 'commercial' | 'mixed';
  };
}

interface VacancyCalendarTableProps {
  data: VacancyCalendarData[];
  onRowAction?: (id: string, action: string) => void;
}

const VacancyCalendarTable: React.FC<VacancyCalendarTableProps> = ({
  data,
  onRowAction = () => {},
}) => {
  const baseClass = 'VacancyCalendarTable';
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Іконки
  const ContactIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 43 42"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M25.2473 20.1646C24.9708 20.1646 24.7339 20.0674 24.5364 19.873C24.3389 19.6785 24.2417 19.4415 24.2447 19.162C24.2417 18.8886 24.3389 18.6546 24.5364 18.4602C24.7339 18.2658 24.9708 18.1685 25.2473 18.1685C25.5147 18.1685 25.7471 18.2658 25.9446 18.4602C26.1451 18.6546 26.2469 18.8886 26.2499 19.162C26.2469 19.3473 26.1983 19.516 26.1041 19.6679C26.0129 19.8198 25.8914 19.9413 25.7395 20.0325C25.5906 20.1206 25.4266 20.1646 25.2473 20.1646Z"
        fill="white"
      />
      <path
        d="M21.9102 20.1646C21.6337 20.1646 21.3967 20.0674 21.1992 19.873C21.0017 19.6785 20.9045 19.4415 20.9076 19.162C20.9045 18.8886 21.0017 18.6546 21.1992 18.4602C21.3967 18.2658 21.6337 18.1685 21.9102 18.1685C22.1775 18.1685 22.4099 18.2658 22.6074 18.4602C22.8079 18.6546 22.9097 18.8886 22.9128 19.162C22.9097 19.3473 22.8611 19.516 22.7669 19.6679C22.6758 19.8198 22.5543 19.9413 22.4023 20.0325C22.2535 20.1206 22.0894 20.1646 21.9102 20.1646Z"
        fill="white"
      />
      <path
        d="M18.573 20.1646C18.2965 20.1646 18.0595 20.0674 17.862 19.873C17.6646 19.6785 17.5673 19.4415 17.5704 19.162C17.5673 18.8886 17.6646 18.6546 17.862 18.4602C18.0595 18.2658 18.2965 18.1685 18.573 18.1685C18.8403 18.1685 19.0728 18.2658 19.2702 18.4602C19.4708 18.6546 19.5725 18.8886 19.5756 19.162C19.5725 19.3473 19.5239 19.516 19.4297 19.6679C19.3386 19.8198 19.2171 19.9413 19.0652 20.0325C18.9163 20.1206 18.7522 20.1646 18.573 20.1646Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.8241 11.8333C13.4729 11.8333 13.1361 11.9728 12.8878 12.2211C12.6395 12.4695 12.5 12.8062 12.5 13.1574V28.8703L15.4166 25.9537C15.5885 25.7817 15.8217 25.6852 16.0648 25.6852H29.5092C29.8604 25.6852 30.1972 25.5457 30.4455 25.2974C30.6938 25.049 30.8333 24.7123 30.8333 24.3611V13.1574C30.8333 12.8062 30.6938 12.4695 30.4455 12.2211C30.1972 11.9728 29.8604 11.8333 29.5092 11.8333H13.8241ZM11.5914 10.9248C12.1836 10.3327 12.9867 10 13.8241 10H29.5092C30.3466 10 31.1497 10.3327 31.7419 10.9248C32.334 11.5169 32.6667 12.32 32.6667 13.1574V24.3611C32.6667 25.1985 32.334 26.0016 31.7419 26.5937C31.1497 27.1858 30.3466 27.5185 29.5092 27.5185H16.4445L12.2315 31.7315C11.9693 31.9937 11.5751 32.0721 11.2325 31.9302C10.89 31.7883 10.6667 31.4541 10.6667 31.0833V13.1574C10.6667 12.32 10.9993 11.5169 11.5914 10.9248Z"
        fill="white"
      />
    </svg>
  );

  const UserInfoIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 43 42"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M29.6667 30V28C29.6667 26.9391 29.2171 25.9217 28.417 25.1716C27.6168 24.4214 26.5316 24 25.4 24H17.9333C16.8017 24 15.7165 24.4214 14.9163 25.1716C14.1162 25.9217 13.6667 26.9391 13.6667 28V30M25.9333 16C25.9333 18.2091 24.0231 20 21.6667 20C19.3102 20 17.4 18.2091 17.4 16C17.4 13.7909 19.3102 12 21.6667 12C24.0231 12 25.9333 13.7909 25.9333 16Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const AddUserIcon = () => (
    <svg
      width="42"
      height="42"
      viewBox="0 0 42 42"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="42" height="42" rx="8" fill="#105E96" />
      <path
        d="M24.9273 30V28C24.9273 26.9391 24.5135 25.9217 23.777 25.1716C23.0405 24.4214 22.0416 24 21 24H14.1273C13.0857 24 12.0868 24.4214 11.3503 25.1716C10.6138 25.9217 10.2 26.9391 10.2 28V30M28.8546 17V23M31.8 20H25.9091M21.4909 16C21.4909 18.2091 19.7326 20 17.5636 20C15.3947 20 13.6364 18.2091 13.6364 16C13.6364 13.7909 15.3947 12 17.5636 12C19.7326 12 21.4909 13.7909 21.4909 16Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const MoreIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="8" cy="3" r="1" fill="#637381" />
      <circle cx="8" cy="8" r="1" fill="#637381" />
      <circle cx="8" cy="13" r="1" fill="#637381" />
    </svg>
  );

  const SortIcon = ({ active = false, direction = 'asc' }) => (
    <div className={`${baseClass}__sort-icon`}>
      <div
        className={`${baseClass}__sort-arrow ${baseClass}__sort-arrow--up ${active && direction === 'asc' ? `${baseClass}__sort-arrow--active` : ''}`}
      />
      <div
        className={`${baseClass}__sort-arrow ${baseClass}__sort-arrow--down ${active && direction === 'desc' ? `${baseClass}__sort-arrow--active` : ''}`}
      />
    </div>
  );

  // Аватар компонент
  const Avatar = ({ name }: { name: string }) => {
    const getInitials = (fullName: string) => {
      return fullName
        .split(' ')
        .map((name) => name.charAt(0))
        .join('')
        .substring(0, 2)
        .toUpperCase();
    };

    const getAvatarColor = (name: string) => {
      const colors = [
        '#FF6B6B',
        '#4ECDC4',
        '#45B7D1',
        '#96CEB4',
        '#FFEAA7',
        '#DDA0DD',
        '#98D8C8',
        '#F7DC6F',
        '#BB8FCE',
        '#85C1E9',
      ];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    };

    return (
      <div
        className={`${baseClass}__avatar`}
        style={{ backgroundColor: getAvatarColor(name) }}
      >
        <span className={`${baseClass}__avatar-initials`}>
          {getInitials(name)}
        </span>
      </div>
    );
  };

  // Status badge компонент
  const StatusBadge = ({ status }: { status: string }) => {
    const getStatusConfig = (status: string) => {
      const configs = {
        PENDING: { label: 'PENDING', className: 'pending' },
        DELAY: { label: 'DELAY', className: 'delay' },
        VACANT: { label: 'VACANT', className: 'vacant' },
        WARNING: { label: 'WARNING', className: 'warning' },
      };
      return configs[status as keyof typeof configs] || configs.PENDING;
    };

    const config = getStatusConfig(status);

    return (
      <div
        className={`${baseClass}__status-badge ${baseClass}__status-badge--${config.className}`}
      >
        {config.label}
      </div>
    );
  };

  // Форматування дати
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // Сортування
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Пагінація
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  const tableHeaders = [
    { key: 'date', label: 'DATE', sortable: true, width: '112px' },
    { key: 'vacancy', label: 'VACANCY', sortable: true, width: '123px' },
    { key: 'tenant', label: 'TENANT', sortable: true, width: '442px' },
    { key: 'unit', label: 'UNIT', sortable: true, width: '91px' },
    { key: 'property', label: 'PROPERTY', sortable: true, width: '200px' },
    { key: 'actions', label: '', sortable: false, width: '48px' },
  ];

  return (
    <div className={baseClass}>
      {/* Table */}
      <div className={`${baseClass}__table`}>
        {/* Header */}
        <div className={`${baseClass}__header`}>
          {tableHeaders.map((header) => (
            <div
              key={header.key}
              className={`${baseClass}__header-cell`}
              style={{ width: header.width }}
              onClick={
                header.sortable ? () => handleSort(header.key) : undefined
              }
            >
              {header.label && (
                <>
                  <span className={`${baseClass}__header-label`}>
                    {header.label}
                  </span>
                  {header.sortable && (
                    <SortIcon
                      active={sortConfig?.key === header.key}
                      direction={sortConfig?.direction}
                    />
                  )}
                </>
              )}
              {header.key === 'actions' && <MoreIcon />}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className={`${baseClass}__body`}>
          {currentData.length > 0 ? (
            currentData.map((row) => (
              <div key={row.id} className={`${baseClass}__row`}>
                {/* Date */}
                <div
                  className={`${baseClass}__cell`}
                  style={{ width: '112px' }}
                >
                  <span className={`${baseClass}__date`}>
                    {formatDate(row.date)}
                  </span>
                </div>

                {/* Vacancy Status */}
                <div
                  className={`${baseClass}__cell`}
                  style={{ width: '123px' }}
                >
                  <StatusBadge status={row.vacancy.status} />
                </div>

                {/* Tenant */}
                <div
                  className={`${baseClass}__cell`}
                  style={{ width: '442px' }}
                >
                  {row.tenant ? (
                    <>)
                      <div className={`${baseClass}__tenant-info`}>
                        <Avatar name={row.tenant.name} />
                        <div className={`${baseClass}__tenant-details`}>
                          <div className={`${baseClass}__tenant-name`}>
                            {row.tenant.name}
                          </div>
                          <div className={`${baseClass}__tenant-email`}>
                            {row.tenant.email}
                          </div>
                        </div>
                      </div>
                      <div className={`${baseClass}__tenant-actions`}>
                        <button
                          className={`${baseClass}__action-btn ${baseClass}__action-btn--contact`}
                        >
                          <ContactIcon />
                        </button>
                        <button
                          className={`${baseClass}__action-btn ${baseClass}__action-btn--info`}
                        >
                          <UserInfoIcon />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className={`${baseClass}__vacant-cell`}>
                      <span className={`${baseClass}__vacant-text`}>-</span>
                      <button
                        className={`${baseClass}__add-user-btn`}
                        onClick={() => onRowAction(row.id, 'add-user')}
                      >
                        <AddUserIcon />
                      </button>
                    </div>
                  )}
                </div>

                {/* Unit */}
                <div className={`${baseClass}__cell`} style={{ width: '91px' }}>
                  <span className={`${baseClass}__unit`}>{row.unit}</span>
                </div>

                {/* Property */}
                <div
                  className={`${baseClass}__cell`}
                  style={{ width: '200px' }}
                >
                  <div className={`${baseClass}__property-info`}>
                    <div className={`${baseClass}__property-name`}>
                      {row.property.name}
                    </div>
                    <div
                      className={`${baseClass}__property-badge ${baseClass}__property-badge--${safeToLowerCase(row.property.type || '')}`}
                    >
                      {(row.property.type || 'No units').toUpperCase()}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className={`${baseClass}__cell`} style={{ width: '48px' }}>
                  <button
                    className={`${baseClass}__more-btn`}
                    onClick={() => onRowAction(row.id, 'menu')}
                  >
                    <MoreIcon />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className={`${baseClass}__empty-state`}>No data available</div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        totalRows={data.length}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    </div>
  );
};

export default VacancyCalendarTable;
